import {
  fetchApplications,
  acceptApplication,
  rejectApplication,
  uploadApplicationFiles,
  createApplication,
  createApplicationImage,
  checkEmailExistence,
  resendInviteApplication,
} from '../util/api';
import { applySuccess } from './auth.duck';
import { transformBadHabitsToArray } from '../util/petHelper';

// Action types
export const FETCH_APPLICATIONS_REQUEST =
  'app/applications/FETCH_APPLICATIONS_REQUEST';
export const FETCH_APPLICATIONS_SUCCESS =
  'app/applications/FETCH_APPLICATIONS_SUCCESS';
export const FETCH_APPLICATIONS_ERROR =
  'app/applications/FETCH_APPLICATIONS_ERROR';

export const APPLICATION_ACTION_REQUEST =
  'app/applications/APPLICATION_ACTION_REQUEST';
export const APPLICATION_ACTION_SUCCESS =
  'app/applications/APPLICATION_ACTION_SUCCESS';
export const APPLICATION_ACTION_ERROR =
  'app/applications/APPLICATION_ACTION_ERROR';

// Application submission action types
export const SUBMIT_APPLICATION_REQUEST =
  'app/applications/SUBMIT_APPLICATION_REQUEST';
export const SUBMIT_APPLICATION_SUCCESS =
  'app/applications/SUBMIT_APPLICATION_SUCCESS';
export const SUBMIT_APPLICATION_ERROR =
  'app/applications/SUBMIT_APPLICATION_ERROR';

// File upload action types
export const UPLOAD_FILES_REQUEST = 'app/applications/UPLOAD_FILES_REQUEST';
export const UPLOAD_FILES_SUCCESS = 'app/applications/UPLOAD_FILES_SUCCESS';
export const UPLOAD_FILES_ERROR = 'app/applications/UPLOAD_FILES_ERROR';

// To prevent duplicate emails
export const VALIDATE_EMAIL_REQUEST = 'app/applications/VALIDATE_EMAIL_REQUEST';
export const VALIDATE_EMAIL_SUCCESS = 'app/applications/VALIDATE_EMAIL_SUCCESS';
export const VALIDATE_EMAIL_ERROR = 'app/applications/VALIDATE_EMAIL_ERROR';

// Initial state
const initialState = {
  applications: [],
  fetchInProgress: false,
  fetchError: null,
  actionInProgress: false,
  actionError: null,
  submitInProgress: false,
  uploadedFiles: [],
  uploadInProgress: false,
  uploadError: null,
  validateEmailInProgress: false,
  validateEmailError: null,
  pagination: {
    total_items: 0,
    total_pages: 0,
    current_page: 1,
    per_page: 20,
  },
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_APPLICATIONS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_APPLICATIONS_SUCCESS:
      return { 
        ...state, 
        fetchInProgress: false, 
        applications: payload.items || [],
        pagination: {
          total_items: payload.total_items || 0,
          total_pages: payload.total_pages || 0,
          current_page: payload.current_page || 1,
          per_page: payload.per_page || 20,
        }
      };
    case FETCH_APPLICATIONS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case APPLICATION_ACTION_REQUEST:
      return { ...state, actionInProgress: true, actionError: null };
    case APPLICATION_ACTION_SUCCESS:
      return { ...state, actionInProgress: false };
    case APPLICATION_ACTION_ERROR:
      return { ...state, actionInProgress: false, actionError: payload };

    case SUBMIT_APPLICATION_REQUEST:
      return { ...state, submitInProgress: true };
    case SUBMIT_APPLICATION_SUCCESS:
      return { ...state, submitInProgress: false };
    case SUBMIT_APPLICATION_ERROR:
      return { ...state, submitInProgress: false };

    case UPLOAD_FILES_REQUEST:
      return { ...state, uploadInProgress: true, uploadError: null };
    case UPLOAD_FILES_SUCCESS:
      return { 
        ...state, 
        uploadInProgress: false, 
        uploadedFiles: [...state.uploadedFiles, ...payload.files] 
      };
    case UPLOAD_FILES_ERROR:
      return { ...state, uploadInProgress: false, uploadError: payload };
    
    case VALIDATE_EMAIL_REQUEST:
      return { ...state, validateEmailInProgress: true, validateEmailError: null };
    case VALIDATE_EMAIL_SUCCESS:
      return { ...state, validateEmailInProgress: false };
    case VALIDATE_EMAIL_ERROR:
      return { ...state, validateEmailInProgress: false, validateEmailError: payload };

    default:
      return state;
  }
}

// Thunks
export const loadApplications = (query = {}) => dispatch => {
  dispatch({ type: FETCH_APPLICATIONS_REQUEST });
  return fetchApplications(query)
    .then(data => {
      dispatch({ type: FETCH_APPLICATIONS_SUCCESS, payload: data });
      return data; // Return the data so it can be used by the caller
    })
    .catch(e => {
      dispatch({
        type: FETCH_APPLICATIONS_ERROR,
        payload: e.message || e,
        error: true,
      });
      throw e; // Re-throw the error so the caller can handle it
    });
};

export const applicationAction = (id, actionType) => dispatch => {
  dispatch({ type: APPLICATION_ACTION_REQUEST });
  const apiCall =
    actionType === 'accept'
      ? acceptApplication(id)
      : actionType === 'reject'
      ? rejectApplication(id)
      : actionType === 'resend-invite'
      ? resendInviteApplication(id)
      : Promise.reject(new Error('Unknown action'));
  return apiCall
    .then(() => {
      dispatch({ type: APPLICATION_ACTION_SUCCESS });
      dispatch(loadApplications());
    })
    .catch(e => {
      dispatch({
        type: APPLICATION_ACTION_ERROR,
        payload: e.message || e,
        error: true,
      });
    });
};

// File upload thunk
export const uploadFiles = (files, applicationId = null, isProfilePhoto = false) => dispatch => {
  dispatch({ type: UPLOAD_FILES_REQUEST });
  return uploadApplicationFiles(files, applicationId, isProfilePhoto)
    .then(data => {
      dispatch({ type: UPLOAD_FILES_SUCCESS, payload: data });
      return data.files; // Return the files array instead of the entire response
    })
    .catch(e => {
      dispatch({
        type: UPLOAD_FILES_ERROR,
        payload: e.message || e,
        error: true,
      });
      throw e;
    });
};

export const emailExists = (email) => async dispatch => {
  dispatch({ type: VALIDATE_EMAIL_REQUEST });
  try {
    const data = await checkEmailExistence(email);
    if (data.exists) {
      const message = 'This email has already been used to apply.';
      dispatch({ type: VALIDATE_EMAIL_ERROR, payload: message, error: true });
      return { valid: false, message };
    }

    dispatch({ type: VALIDATE_EMAIL_SUCCESS });
    return { valid: true };
  } catch (err) {
    dispatch({ type: VALIDATE_EMAIL_ERROR, payload: err.message || err, error: true });
    return { valid: false, message: err.message || 'Error validating email' };
  }
};

export const submitApplication = (values) => async dispatch => {
  dispatch({ type: SUBMIT_APPLICATION_REQUEST });
  
  try {
    // 1. Upload profile photo
    let profileUrl = null;
    if (values.primaryPhoto) {
      const uploaded = await dispatch(uploadFiles([values.primaryPhoto], null, true));
      profileUrl = uploaded[0]?.url || uploaded[0]?.key || uploaded[0];
    }

    // 2. Upload pet images and collect info for pet_images table
    let petImages = [];
    let pets = Array.isArray(values.yourPets) ? values.yourPets : [];
    pets = await Promise.all(pets.map(async (pet, petIndex) => {
      let petPhotoUrls = [];
      if (pet.petPhotos && Array.isArray(pet.petPhotos) && pet.petPhotos.length > 0) {
        const uploaded = await dispatch(uploadFiles(pet.petPhotos, null, false));
        petPhotoUrls = uploaded.map(f => ({
          url: f.url || f.key || f,
          original_filename: f.original_filename || f.name || null,
          pet_index: petIndex
        }));
        petImages = petImages.concat(
          petPhotoUrls.map(img => ({
            pet_index: petIndex,
            url: img.url,
            original_filename: img.original_filename
          }))
        );
      }
      const { petPhotos, ...petRest } = pet;
      
      // Transform bad habits from individual fields to array format
      if (petRest.badHabits_none || petRest.badHabits_other || Object.keys(petRest).some(key => key.startsWith('badHabits_') && key !== 'badHabits_none' && key !== 'badHabits_other' && key !== 'badHabits_otherText' && petRest[key] > 0)) {
        const badHabitsArray = transformBadHabitsToArray(petRest, 'badHabits_');
        
        // Replace individual fields with the array
        const { 
          badHabits_demandBarking, badHabits_destructiveChewing, badHabits_excessiveBarking, 
          badHabits_preyDrive, badHabits_reactivityBarrier, badHabits_reactivityOtherDogs, 
          badHabits_reactivityPeople, badHabits_reactivityLeash, badHabits_resourceGuardingFood, 
          badHabits_resourceGuardingPeople, badHabits_resourceGuardingToys, badHabits_generalAnxiety, 
          badHabits_separationAnxiety, badHabits_velcro, badHabits_none, badHabits_other, 
          badHabits_otherText, ...cleanPetRest 
        } = petRest;
        
        return {
          ...cleanPetRest,
          badHabits: badHabitsArray,
          badHabitsOtherText: petRest.badHabits_otherText || ''
        };
      }
      
      return petRest;
    }));

    // 3. Prepare application data
    const applicationData = {
      application_type: values.application_type,
      email: values.email,
      first_name: values.firstName,
      last_name: values.lastName,
      join_date: new Date().toISOString().slice(0, 10),
      status: 'pending',
      profile_url: profileUrl,
      questionnaire: {
        ...values,
        yourPets: pets
      }
    };

    // 4. Create application (returns application id)
    const appResponse = await createApplication(applicationData);
    const applicationId = appResponse.id || appResponse.data?.id;

    // 5. Insert pet images (if any)
    if (applicationId && petImages.length > 0) {
      await Promise.all(
        petImages.map(img =>
          createApplicationImage({
            application_id: applicationId,
            pet_index: img.pet_index,
            url: img.url,
            original_filename: img.original_filename
          })
        )
      );
    }

    // 6. Upload additional documents to application files (if any)
    if (applicationId && values.applicationFiles?.uploadedFiles && Array.isArray(values.applicationFiles.uploadedFiles) && values.applicationFiles.uploadedFiles.length > 0) {
      await uploadApplicationFiles(values.applicationFiles.uploadedFiles, applicationId, false);
    }

    dispatch({ type: SUBMIT_APPLICATION_SUCCESS });
    
    // Dispatch success to auth duck to show success message
    dispatch(applySuccess());
    
    return { success: true, applicationId };
  } catch (err) {
    dispatch({
      type: SUBMIT_APPLICATION_ERROR,
      error: true,
    });
    throw err;
  }
};

export const loadData = (params, search) => dispatch => {
  dispatch({ type: FETCH_APPLICATIONS_REQUEST });
  return fetchApplications({ perPage: 100 })
    .then(data => {
      dispatch({ type: FETCH_APPLICATIONS_SUCCESS, payload: data });
      return data; // Return the data so it can be used by the caller
    })
    .catch(e => {
      dispatch({
        type: FETCH_APPLICATIONS_ERROR,
        payload: e.message || e,
        error: true,
      });
      throw e; // Re-throw the error so the caller can handle it
    });
};